import { Injectable, Type } from '@angular/core';
import { IBaseElement } from '../models/base-element';
import { ElementType } from '../models/element-type';
import { Page } from '../models/page';
import { Table } from '../models/table';
import { Tabs } from '../models/tabs';
import { Wizard } from '../models/wizard';

// Lazy component imports to avoid circular dependencies
const getComponentType = async (elementType: ElementType): Promise<Type<any> | null> => {
  switch (elementType) {
    case ElementType.button:
      return (await import('../components/dynamic-elements/button/button.component')).ButtonComponent;
    case ElementType.chart:
      return (await import('../components/dynamic-elements/chart/chart.component')).ChartComponent;
    case ElementType.condition:
      return (await import('../components/dynamic-elements/condition/condition.component')).ConditionComponent;
    case ElementType.container:
      return (await import('../components/dynamic-elements/container/container.component')).ContainerComponent;
    case ElementType.form:
      return (await import('../components/dynamic-elements/form/form.component')).FormComponent;
    case ElementType.input:
      return (await import('../components/dynamic-elements/input/input.component')).InputComponent;
    case ElementType.list:
      return (await import('../components/dynamic-elements/list/list.component')).ListComponent;
    case ElementType.confirm:
      return (await import('../components/dynamic-elements/modal-confirm/modal-confirm.component')).ModalConfirmComponent;
    case ElementType.modal:
      return (await import('../components/dynamic-elements/modal-default/modal-default.component')).ModalDefaultComponent;
    case ElementType.numpad:
      return (await import('../components/dynamic-elements/numpad/numpad.component')).NumpadComponent;
    case ElementType.page:
      return (await import('../components/dynamic-elements/page/page.component')).PageComponent;
    case ElementType.pagination:
      return (await import('../components/dynamic-elements/pagination/pagination.component')).PaginationComponent;
    case ElementType.refresh:
      return (await import('../components/dynamic-elements/refresh/refresh.component')).RefreshComponent;
    case ElementType.scanner:
      return (await import('../components/dynamic-elements/scanner/scanner.component')).ScannerComponent;
    case ElementType.section:
      return (await import('../components/dynamic-elements/section/section.component')).SectionComponent;
    case ElementType.signature:
      return (await import('../components/dynamic-elements/signature/signature.component')).SignatureComponent;
    case ElementType.table:
      return (await import('../components/dynamic-elements/table/table.component')).TableComponent;
    case ElementType.tabs:
      return (await import('../components/dynamic-elements/tabs/tabs.component')).TabsComponent;
    case ElementType.text:
      return (await import('../components/dynamic-elements/text/text.component')).TextComponent;
    case ElementType.timeline:
      return (await import('../components/dynamic-elements/timeline/timeline.component')).TimelineComponent;
    case ElementType.widgetNavigation:
      return (await import('../components/dynamic-elements/widget-navigation/widget-navigation.component')).WidgetNavigationComponent;
    case ElementType.wizard:
      return (await import('../components/dynamic-elements/wizard/wizard.component')).WizardComponent;
    case ElementType.geolocation:
      return (await import('../components/dynamic-elements/geolocation/geolocation.component')).GeolocationComponent;
    case ElementType.footer:
      return (await import('../components/footer/footer.component')).FooterComponent;
    case ElementType.menu:
      return (await import('../components/menu/menu.component')).MenuComponent;
    default:
      return null;
  }
};

@Injectable({
  providedIn: 'root',
})
export class ElementService {
  public async initializeElement(element: IBaseElement | undefined): Promise<IBaseElement> {
    if (!element) {
      return {} as IBaseElement;
    }
    if (element.componentType) {
      return element;
    }

    const componentType = await getComponentType(element.elementType);
    if (componentType) {
      element.componentType = componentType;
    }

    // Handle special cases with nested elements
    switch (element.elementType) {
      case ElementType.table:
        const table = element as Table;
        for (let row of table.rows) {
          await this.initializeElement(row);
          await this.initializeElements(row.data);
        }
        break;
      case ElementType.page:
        const page = element as Page;
        await this.initializeElements(page.sections);
        await this.initializeElements(page.infoContent);
        await this.initializeElements(page.tipContent);
        break;
      case ElementType.tabs:
        const tabs = element as Tabs;
        await this.initializeElements(tabs.tabList);
        break;
      case ElementType.wizard:
        const wizard = element as Wizard;
        await this.initializeElements(wizard.steps);
        break;
    }

    await this.initializeElements(element.content);
    return element;
  }

  public async initializeElements(elements: IBaseElement[] | null): Promise<void> {
    for (var element of elements ?? []) {
      await this.initializeElement(element);
    }
  }
}
